import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, BadgeCheck, Link2, Copy, Undo2, XCircle, ExternalLink, ShieldCheck } from 'lucide-react';
import { fetchJson, formatMoney as money } from '@/lib/api';
import SelleCheckBankChecks from '@/components/SelleCheckBankChecks';

const PROTECCIONES = [
  { id: 'none', label: 'Standard', help: 'A chargeback comes out of your Stripe balance.' },
  { id: 'guarantee', label: 'With guarantee', help: 'If you lose a chargeback, SelleSend pays you back the amount and the dispute fee.' },
];

const ESTADOS = {
  open: 'Waiting for payment', checkout: 'Buyer is paying', capturing: 'Processing',
  paid: 'Paid', partially_refunded: 'Partly refunded', refunded: 'Refunded', canceled: 'Canceled',
};

const VACIO = { amount: '', description: '', buyer_email: '' };

/**
 * SelleCheck: one merchant pays another.
 * By card: the seller connects its own Stripe account, creates a payment and shares the link.
 * The charge is made in the seller's Stripe account: Stripe takes its fee there,
 * and SelleSend takes its fee from the sale. The buyer pays the amount shown.
 * By bank: a merchant sends a check from its bank account (SelleCheckBankChecks).
 */
export default function SelleCheckPage() {
  const { token } = useAuth();
  const [params] = useSearchParams();

  const [cuenta, setCuenta] = useState(null);
  const [tabla, setTabla] = useState(null);
  const [cobros, setCobros] = useState([]);
  const [errorCarga, setErrorCarga] = useState('');
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState('');
  const [form, setForm] = useState(VACIO);
  const [proteccion, setProteccion] = useState('none');
  const [estimado, setEstimado] = useState(null);
  const [devolver, setDevolver] = useState(null); // { id, amount, reason }

  const cargar = useCallback(async () => {
    try {
      const [a, f, p] = await Promise.all([
        fetchJson('/merchant-payments/account', { token }),
        fetchJson('/merchant-payments/fees', { token }),
        fetchJson('/merchant-payments', { token }),
      ]);
      setCuenta(a);
      setTabla(f);
      setCobros(p.payments || []);
      setErrorCarga('');
    } catch (e) {
      setErrorCarga(e.message);
    } finally {
      setCargando(false);
    }
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  // El rango lo calcula el servidor, con las mismas reglas con que cobra.
  const monto = Number(form.amount || 0);
  const montoValido = tabla && monto >= tabla.minimum_sale && monto <= tabla.maximum_sale;
  useEffect(() => {
    if (!montoValido) { setEstimado(null); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetchJson(`/merchant-payments/estimate?amount=${monto}&protection=${proteccion}`, { token, signal: ctrl.signal })
        .then(setEstimado)
        .catch(() => setEstimado(null));
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [monto, proteccion, montoValido, token]);

  const correr = async (clave, fn) => {
    setOcupado(clave);
    try {
      return await fn();
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setOcupado('');
    }
  };

  const alta = () => correr('alta', async () => {
    const d = await fetchJson('/merchant-payments/account/onboarding', {
      token, method: 'POST', body: { origin_url: window.location.origin },
    });
    window.location.href = d.url;
  });

  const panel = () => correr('panel', async () => {
    const d = await fetchJson('/merchant-payments/account/dashboard', { token, method: 'POST', body: {} });
    window.open(d.url, '_blank', 'noopener');
  });

  const crear = () => correr('crear', async () => {
    await fetchJson('/merchant-payments', {
      token, method: 'POST',
      body: { amount: monto, description: form.description, protection: proteccion, buyer_email: form.buyer_email || null },
    });
    toast.success('Payment created. Send the link to the buyer.');
    setForm(VACIO);
    cargar();
  });

  const cancelar = (p) => correr(`cancel-${p.id}`, async () => {
    await fetchJson(`/merchant-payments/${p.id}/cancel`, { token, method: 'POST', body: {} });
    toast.success('Payment canceled.');
    cargar();
  });

  const reembolsar = () => correr(`refund-${devolver.id}`, async () => {
    await fetchJson(`/merchant-payments/${devolver.id}/refund`, {
      token, method: 'POST', body: { amount: Number(devolver.amount), reason: devolver.reason },
    });
    toast.success('Refund sent.');
    setDevolver(null);
    cargar();
  });

  const copiar = async (p) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${p.pay_path}`);
      toast.success('Payment link copied.');
    } catch {
      toast.error(`Copy this link: ${window.location.origin}${p.pay_path}`);
    }
  };

  if (cargando) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  }
  if (errorCarga) {
    return <p className="text-sm text-red-600 py-8 text-center" data-testid="sellecheck-error">{errorCarga}</p>;
  }

  const lista = cuenta?.ready;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="sellecheck-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><BadgeCheck className="w-6 h-6 text-emerald-600" /> SelleCheck</h1>
        <p className="text-slate-500 text-sm mt-1">
          Charge another merchant by card, or send a check from your bank. What you receive goes straight into your Stripe account.
        </p>
      </div>

      {/* Payouts */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="payouts-card">
        <h3 className="font-semibold">Your Stripe account</h3>
        {!cuenta?.connected && (
          <p className="text-sm text-slate-500 mt-1">Connect a Stripe account so other merchants can pay you, by card or by check. You can use one you already have or open one; Stripe asks for your business and bank details.</p>
        )}
        {cuenta?.connected && !lista && (
          <p className="text-sm text-amber-700 mt-1">Stripe still needs details before you can take payments.</p>
        )}
        {lista && (
          <p className="text-sm text-emerald-700 mt-1">
            Ready to take payments.{cuenta.payouts_enabled ? ' Payouts to your bank are on.' : ' Payouts to your bank are not on yet.'}
          </p>
        )}
        <div className="flex gap-2 mt-3">
          {!lista && (
            <Button onClick={alta} disabled={!!ocupado} data-testid="setup-payouts">
              {ocupado === 'alta' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {cuenta?.connected ? 'Finish setup with Stripe' : 'Connect Stripe'}
            </Button>
          )}
          {cuenta?.connected && (
            <Button variant="outline" onClick={panel} disabled={!!ocupado}>
              <ExternalLink className="w-4 h-4 mr-1" /> Stripe dashboard
            </Button>
          )}
        </div>
        {params.get('onboarding') === 'return' && !lista && (
          <p className="text-xs text-slate-500 mt-2">If you just finished, Stripe may take a minute to review. Reload this page.</p>
        )}
      </div>

      <SelleCheckBankChecks token={token} canReceive={!!lista} />

      {/* Fees */}
      {tabla && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="fees-card">
          <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> What a card sale costs you</h3>
          <table className="w-full text-sm mt-3">
            <thead className="text-xs uppercase text-slate-500">
              <tr><th className="text-left py-1"></th><th className="text-right">Domestic</th><th className="text-right">International</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-100 dark:border-slate-800"><td className="py-1">SelleCheck fee</td><td className="text-right">{tabla.rates.domestic.base}%</td><td className="text-right">{tabla.rates.international.base}%</td></tr>
              <tr className="border-t border-slate-100 dark:border-slate-800"><td className="py-1">Guarantee (optional)</td><td className="text-right">+{tabla.rates.domestic.guarantee}%</td><td className="text-right">+{tabla.rates.international.guarantee}%</td></tr>
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-2">
            Minimum fee {money(tabla.minimum_fee)} per sale. {tabla.stripe_fee_rule} {tabla.scope_rule} The exact fee is set when the buyer pays, from the card used.
          </p>
        </div>
      )}

      {/* New payment */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="new-payment-card">
        <h3 className="font-semibold">Ask for a card payment</h3>
        {!lista ? (
          <p className="text-sm text-slate-500 mt-1">Connect Stripe first.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div>
              <Label htmlFor="sc-amount">Amount (USD)</Label>
              <Input id="sc-amount" type="number" min={tabla?.minimum_sale} max={tabla?.maximum_sale} step="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
            </div>
            <div>
              <Label htmlFor="sc-buyer">Buyer email (optional)</Label>
              <Input id="sc-buyer" type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="sc-description">What is it for</Label>
              <Input id="sc-description" maxLength={200} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                     placeholder="Rolex Submariner 126610LN, order 1042" data-testid="pay-description" />
            </div>
            <div className="md:col-span-2 grid sm:grid-cols-2 gap-2">
              {PROTECCIONES.map((p) => (
                <button key={p.id} type="button" onClick={() => setProteccion(p.id)}
                        className={`text-left rounded-lg border p-3 text-sm ${proteccion === p.id ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30' : 'border-slate-200 dark:border-slate-700'}`}
                        data-testid={`protection-${p.id}`}>
                  <b>{p.label}</b>
                  <p className="text-xs text-slate-500 mt-0.5">{p.help}</p>
                </button>
              ))}
            </div>
            <div className="md:col-span-2 flex items-center justify-between gap-4">
              <p className="text-sm text-slate-600" data-testid="pay-estimate">
                {estimado && montoValido
                  ? <>SelleCheck fee between <b>{money(estimado.fee_min)}</b> and <b>{money(estimado.fee_max)}</b>, depending on the card, plus Stripe's own fee.</>
                  : tabla ? `From ${money(tabla.minimum_sale)} to ${money(tabla.maximum_sale)}.` : ''}
              </p>
              <Button onClick={crear} disabled={!!ocupado || !montoValido || !form.description.trim()} data-testid="create-payment">
                {ocupado === 'crear' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Create payment link
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="payments-list">
        <h3 className="font-semibold mb-3">Your payments</h3>
        {cobros.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">No payments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-1">Date</th><th className="text-left">For</th><th className="text-right">Amount</th>
                  <th className="text-right">SelleCheck fee</th><th className="text-left pl-3">Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {cobros.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`payment-${p.id}`}>
                    <td className="py-2 whitespace-nowrap">{(p.created_at || '').slice(0, 10)}</td>
                    <td className="max-w-[16rem] truncate" title={p.description}>{p.description}</td>
                    <td className="text-right tabular-nums">{money(p.amount)}</td>
                    <td className="text-right tabular-nums">
                      {p.fee_amount !== undefined ? money(p.fee_amount) : <span className="text-slate-400">{money(p.estimate.fee_min)}–{money(p.estimate.fee_max)}</span>}
                    </td>
                    <td className="pl-3 whitespace-nowrap">
                      {ESTADOS[p.status] || p.status}
                      {p.refunded_amount > 0 && <span className="text-xs text-slate-500"> · {money(p.refunded_amount)} back</span>}
                      {(p.disputes || []).length > 0 && <span className="text-xs text-red-600"> · dispute</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {['open', 'checkout'].includes(p.status) && (
                        <Button size="sm" variant="ghost" onClick={() => copiar(p)} title="Copy payment link"><Copy className="w-4 h-4" /></Button>
                      )}
                      {p.status === 'open' && (
                        <Button size="sm" variant="ghost" onClick={() => cancelar(p)} disabled={!!ocupado} title="Cancel"><XCircle className="w-4 h-4" /></Button>
                      )}
                      {['paid', 'partially_refunded'].includes(p.status) && (
                        <Button size="sm" variant="ghost" title="Refund" disabled={!!ocupado}
                                onClick={() => setDevolver({ id: p.id, amount: (p.amount - p.refunded_amount).toFixed(2), reason: '' })}>
                          <Undo2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {devolver && (
          <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-4 grid sm:grid-cols-[8rem_1fr_auto_auto] gap-2 items-end" data-testid="refund-form">
            <div>
              <Label htmlFor="sc-refund-amount">Refund (USD)</Label>
              <Input id="sc-refund-amount" type="number" step="0.01" min="0.01" value={devolver.amount}
                     onChange={(e) => setDevolver({ ...devolver, amount: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="sc-refund-reason">Why</Label>
              <Input id="sc-refund-reason" maxLength={200} value={devolver.reason}
                     onChange={(e) => setDevolver({ ...devolver, reason: e.target.value })} />
            </div>
            <Button onClick={reembolsar} disabled={!!ocupado || !devolver.reason.trim() || !(Number(devolver.amount) > 0)}>
              {ocupado.startsWith('refund-') && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Send refund
            </Button>
            <Button variant="ghost" onClick={() => setDevolver(null)}>Close</Button>
            <p className="text-xs text-slate-500 sm:col-span-4">The money comes out of your Stripe balance. The SelleCheck fee is not returned.</p>
          </div>
        )}

        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1"><Link2 className="w-3 h-3" /> The buyer pays from the link; no SelleSend account needed.</p>
      </div>
    </div>
  );
}
